import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowLeft, Clock, LogIn, LogOut } from 'lucide-react';
import { formatTimeFromISO } from '@/utils/timezoneUtils';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatUtc } from '@/utils/dateUtils';

function StaffSessionsPage() {
  const { t, isRTL } = useLanguage();
  const { date } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchSessions();
  }, [date]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/staff/sessions/${date}`, {
        withCredentials: true
      });
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      setError(err.response?.data?.detail || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return formatTimeFromISO(dateStr);
  };

    const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatUtc(dateStr, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };


  const formatWorkingTime = (minutes) => {
    if (minutes === null || minutes === undefined) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sessions Detail</h1>
          <p className="text-gray-500">{formatDate(date)}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="total-checkins-card">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-full">
              <LogIn className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Check-ins</p>
              <p className="text-3xl font-bold text-gray-900">{data?.summary?.total_check_ins || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="total-checkouts-card">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-full">
              <LogOut className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Check-outs</p>
              <p className="text-3xl font-bold text-gray-900">{data?.summary?.total_check_outs || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="total-hours-card">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-full">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Working Hours</p>
              <p className="text-3xl font-bold text-gray-900">{data?.summary?.total_working_hours || 0}h</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sessions Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">All Sessions</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="font-medium">Session</TableHead>
              <TableHead className="font-medium">{t("attendance.check_in")}</TableHead>
              <TableHead className="font-medium">{t("attendance.check_out")}</TableHead>
              <TableHead className="font-medium">Working Time</TableHead>
              <TableHead className="font-medium">{t("common.status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.sessions?.length > 0 ? (
              data.sessions.map((session, index) => (
                <TableRow key={session.attendance_id || index} data-testid={`session-row-${index}`}>
                  <TableCell className="font-medium">
                    <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-sm">
                      Session {session.session_number || index + 1}
                    </span>
                  </TableCell>
                  <TableCell className="text-green-600 font-medium">
                    {formatTime(session.check_in)}
                  </TableCell>
                  <TableCell className="text-red-600 font-medium">
                    {formatTime(session.check_out)}
                  </TableCell>
                  <TableCell>
                    {session.working_minutes !== null ? (
                      <span className="font-medium">{formatWorkingTime(session.working_minutes)}</span>
                    ) : (
                      <span className="text-gray-400">In progress</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      session.status === 'present' ? 'bg-green-100 text-green-700' :
                      session.status === 'late' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {session.status}
                      {session.late_by > 0 && ` (${session.late_by}m late)`}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                  No sessions found for this date
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default StaffSessionsPage;
